import React, { useState, useEffect } from 'react';
import { getDatabase, ref, push, set, update, serverTimestamp, get } from "firebase/database";
import { getAuth } from 'firebase/auth';
import FileUploader from './FileUploader';
import MiniFileUploader from './MiniFileUploader';
import EventBuilder from './EventBuilder';
import './ProductCreator.css';

const ProductCreator = ({ onClose, onProductCreated, salesSettings, preselectedType, editMode = false, existingProduct = null }) => {
    const [productData, setProductData] = useState(
        editMode && existingProduct ? {
            type: existingProduct.type || preselectedType || 'digital',
            subtype: existingProduct.subtype || '',
            title: existingProduct.title || '',
            description: existingProduct.description || '',
            price: existingProduct.price || '',
            currency: existingProduct.currency || salesSettings?.defaultCurrency || 'EUR',
            contentType: existingProduct.contentType || 'download',
            contentData: existingProduct.contentData || {},
            maxPurchases: existingProduct.maxPurchases || null,
            accessDuration: existingProduct.accessDuration || null,
            status: existingProduct.status || 'draft',
            // Stripe-specific fields
            taxBehavior: existingProduct.taxBehavior || 'exclusive',
            taxCode: existingProduct.taxCode || '',
            shippable: existingProduct.shippable || false,
            statementDescriptor: existingProduct.statementDescriptor || '',
            unitLabel: existingProduct.unitLabel || '',
            billingScheme: existingProduct.billingScheme || 'per_unit',
            // SEO & Marketing
            metaTitle: existingProduct.metaTitle || '',
            metaDescription: existingProduct.metaDescription || '',
            features: existingProduct.features || [],
            tags: existingProduct.tags || [],
            // Discount fields
            showDiscount: existingProduct.showDiscount || false,
            discountType: existingProduct.discountType || 'percentage',
            discountValue: existingProduct.discountValue || '',
            discountTimeLimited: existingProduct.discountTimeLimited || false,
            discountExpiryDate: existingProduct.discountExpiryDate || '',
            discountLabel: existingProduct.discountLabel || 'SAVE'
        } : {
            type: preselectedType || 'digital',
            subtype: '',
            title: '',
            description: '',
            price: '',
            currency: salesSettings?.defaultCurrency || 'EUR',
            contentType: 'download',
            contentData: {},
            maxPurchases: null,
            accessDuration: null,
            status: 'draft',
            // Stripe-specific fields
            taxBehavior: 'exclusive', // inclusive, exclusive, unspecified
            taxCode: '', // Stripe tax code for proper tax calculation
            shippable: false, // Digital products are not shippable
            statementDescriptor: '', // What appears on customer's credit card statement
            unitLabel: '', // e.g., "per seat", "per user", "per month"
            billingScheme: 'per_unit', // per_unit or tiered
            // SEO & Marketing
            metaTitle: '',
            metaDescription: '',
            features: [], // Array of feature strings
            tags: [], // Array of tags for categorization
            // Discount fields
            showDiscount: false,
            discountType: 'percentage',
            discountValue: '',
            discountTimeLimited: false,
            discountExpiryDate: '',
            discountLabel: 'SAVE'
        }
    );
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [uploadedFiles, setUploadedFiles] = useState(
        editMode && existingProduct?.contentData?.uploadedFiles ? 
        existingProduct.contentData.uploadedFiles : []
    );

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;

    // Get product type display info
    const getProductTypeInfo = (type) => {
        const isEditing = editMode && existingProduct;
        switch (type) {
            case 'digital':
                return {
                    title: isEditing ? 'Edit Digital Product' : 'Create Digital Product',
                    subtitle: isEditing ? 'Update your downloadable content' : 'Set up your downloadable content',
                    icon: '📱'
                };
            case 'course':
                return {
                    title: isEditing ? 'Edit Online Course' : 'Create Online Course',
                    subtitle: isEditing ? 'Update your educational content' : 'Build your educational content',
                    icon: '🎥'
                };
            case 'event':
                return {
                    title: isEditing ? 'Edit Live Event' : 'Host Live Event',
                    subtitle: isEditing ? 'Update your live session' : 'Schedule your live session',
                    icon: '🎟️'
                };
            default:
                return {
                    title: isEditing ? 'Edit Product' : 'Create New Product',
                    subtitle: isEditing ? 'Update your product' : 'Set up your product',
                    icon: '📦'
                };
        }
    };

    // Product subtypes based on main type
    const productSubtypes = {
        digital: [
            { value: 'ebook', label: 'E-Book / PDF Guide', icon: '📖' },
            { value: 'template', label: 'Template / Design Asset', icon: '🎨' },
            { value: 'software', label: 'Software / App', icon: '💻' },
            { value: 'audio', label: 'Audio / Music', icon: '🎵' },
            { value: 'video', label: 'Video Content', icon: '🎬' },
            { value: 'course_materials', label: 'Course Materials', icon: '📚' },
            { value: 'preset', label: 'Presets / Settings', icon: '⚙️' },
            { value: 'other', label: 'Other Digital Product', icon: '📦' }
        ],
        course: [
            { value: 'online_course', label: 'Complete Online Course', icon: '🎓' },
            { value: 'mini_course', label: 'Mini Course / Tutorial Series', icon: '📝' },
            { value: 'masterclass', label: 'Masterclass', icon: '👨‍🏫' },
            { value: 'certification', label: 'Certification Program', icon: '🏆' },
            { value: 'workshop_series', label: 'Workshop Series', icon: '🔨' }
        ],
        event: [
            { value: 'conference', label: 'Multi-Day Conference', icon: '🎤' },
            { value: 'workshop', label: 'Workshop Series', icon: '🛠️' },
            { value: 'seminar', label: 'Educational Seminar', icon: '🎓' },
            { value: 'networking', label: 'Networking Event', icon: '🤝' },
            { value: 'retreat', label: 'Retreat / Intensive', icon: '🏔️' },
            { value: 'festival', label: 'Festival / Celebration', icon: '🎪' }
        ]
    };

    const validateForm = () => {
        const newErrors = {};

        // Basic validation
        if (!productData.subtype.trim()) {
            newErrors.subtype = 'Please select a product category';
        }
        if (!productData.title.trim()) {
            newErrors.title = 'Product title is required';
        }
        if (!productData.description.trim()) {
            newErrors.description = 'Product description is required';
        }

        // Price validation for non-event products
        if (productData.type !== 'event') {
            if (!productData.price && productData.price !== 0) {
                newErrors.price = 'Price is required';
            }
            if (productData.price < 0) {
                newErrors.price = 'Price cannot be negative';
            }
        }

        // Content validation
        if (productData.type === 'digital' && !productData.contentData.downloadUrls?.length && uploadedFiles.length === 0) {
            newErrors.content = 'Please upload at least one file or provide a download URL';
        }
        if (productData.type === 'course' && !productData.contentData.videoModules?.length) {
            newErrors.content = 'At least one video module is required';
        }
        if (productData.type === 'event') {
            if (!productData.contentData.eventDays?.length) {
                newErrors.content = 'At least one event day is required';
            } else if (!productData.contentData.tickets?.length) {
                newErrors.content = 'At least one ticket type is required';
            } else if (!productData.contentData.eventType) {
                newErrors.content = 'Please specify if the event is digital or in-person';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field, value) => {
        setProductData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleContentDataChange = (field, value) => {
        setProductData(prev => ({
            ...prev,
            contentData: {
                ...prev.contentData,
                [field]: value
            }
        }));
    };

    // File handling functions
    const handleFileUploaded = (fileData) => {
        const newFiles = [...uploadedFiles, fileData];
        setUploadedFiles(newFiles);
        
        // Update contentData with uploaded files
        const fileUrls = newFiles.map(file => file.url);
        handleContentDataChange('uploadedFiles', newFiles);
        handleContentDataChange('downloadUrls', fileUrls);
    };

    const removeUploadedFile = (index) => {
        const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
        setUploadedFiles(updatedFiles);
        
        // Update contentData
        const fileUrls = updatedFiles.map(file => file.url);
        handleContentDataChange('uploadedFiles', updatedFiles);
        handleContentDataChange('downloadUrls', fileUrls);
    };

    // Video module functions (same as before)
    const addVideoModule = () => {
        const currentModules = productData.contentData.videoModules || [];
        handleContentDataChange('videoModules', [...currentModules, { 
            title: '', 
            url: '', 
            description: '',
            duration: '',
            documents: [],
            featurePoints: []
        }]);
    };

    const updateVideoModule = (index, field, value) => {
        const currentModules = productData.contentData.videoModules || [];
        const updatedModules = [...currentModules];
        updatedModules[index] = {
            ...updatedModules[index],
            [field]: value
        };
        handleContentDataChange('videoModules', updatedModules);
    };

    const removeVideoModule = (index) => {
        const currentModules = productData.contentData.videoModules || [];
        const updatedModules = currentModules.filter((_, i) => i !== index);
        handleContentDataChange('videoModules', updatedModules);
    };

    const handleModuleDocumentUploaded = (moduleIndex, documentData) => {
        const currentModules = productData.contentData.videoModules || [];
        const updatedModules = [...currentModules];
        const currentDocs = updatedModules[moduleIndex].documents || [];
        updatedModules[moduleIndex] = {
            ...updatedModules[moduleIndex],
            documents: [...currentDocs, documentData]
        };
        handleContentDataChange('videoModules', updatedModules);
    };

    const removeModuleDocument = (moduleIndex, docIndex) => {
        const currentModules = productData.contentData.videoModules || [];
        const updatedModules = [...currentModules];
        updatedModules[moduleIndex].documents = updatedModules[moduleIndex].documents.filter((_, i) => i !== docIndex);
        handleContentDataChange('videoModules', updatedModules);
    };

    // Feature Points functions
    const addModuleFeaturePoint = (moduleIndex) => {
        const currentModules = productData.contentData.videoModules || [];
        const updatedModules = [...currentModules];
        const currentFeatures = updatedModules[moduleIndex].featurePoints || [];
        updatedModules[moduleIndex] = {
            ...updatedModules[moduleIndex],
            featurePoints: [...currentFeatures, { text: '', icon: 'fas fa-check' }]
        };
        handleContentDataChange('videoModules', updatedModules);
    };

    const updateModuleFeaturePoint = (moduleIndex, featureIndex, field, value) => {
        const currentModules = productData.contentData.videoModules || [];
        const updatedModules = [...currentModules];
        const updatedFeatures = [...(updatedModules[moduleIndex].featurePoints || [])];
        updatedFeatures[featureIndex] = {
            ...updatedFeatures[featureIndex],
            [field]: value
        };
        updatedModules[moduleIndex] = {
            ...updatedModules[moduleIndex],
            featurePoints: updatedFeatures
        };
        handleContentDataChange('videoModules', updatedModules);
    };

    const removeModuleFeaturePoint = (moduleIndex, featureIndex) => {
        const currentModules = productData.contentData.videoModules || [];
        const updatedModules = [...currentModules];
        updatedModules[moduleIndex].featurePoints = (updatedModules[moduleIndex].featurePoints || []).filter((_, i) => i !== featureIndex);
        handleContentDataChange('videoModules', updatedModules);
    };

    // Event handling functions (same as before)
    const addEventDay = () => {
        const currentDays = productData.contentData.eventDays || [];
        handleContentDataChange('eventDays', [...currentDays, {
            date: '',
            title: '',
            description: '',
            timeSlots: []
        }]);
    };

    const updateEventDay = (dayIndex, field, value) => {
        const currentDays = productData.contentData.eventDays || [];
        const updatedDays = [...currentDays];
        updatedDays[dayIndex] = {
            ...updatedDays[dayIndex],
            [field]: value
        };
        handleContentDataChange('eventDays', updatedDays);
    };

    const addTimeSlot = (dayIndex) => {
        const currentDays = productData.contentData.eventDays || [];
        const updatedDays = [...currentDays];
        const currentSlots = updatedDays[dayIndex].timeSlots || [];
        updatedDays[dayIndex].timeSlots = [...currentSlots, {
            startTime: '',
            endTime: '',
            title: '',
            description: ''
        }];
        handleContentDataChange('eventDays', updatedDays);
    };

    const updateTimeSlot = (dayIndex, slotIndex, field, value) => {
        const currentDays = productData.contentData.eventDays || [];
        const updatedDays = [...currentDays];
        updatedDays[dayIndex].timeSlots[slotIndex] = {
            ...updatedDays[dayIndex].timeSlots[slotIndex],
            [field]: value
        };
        handleContentDataChange('eventDays', updatedDays);
    };

    const removeTimeSlot = (dayIndex, slotIndex) => {
        const currentDays = productData.contentData.eventDays || [];
        const updatedDays = [...currentDays];
        updatedDays[dayIndex].timeSlots = updatedDays[dayIndex].timeSlots.filter((_, i) => i !== slotIndex);
        handleContentDataChange('eventDays', updatedDays);
    };

    const removeEventDay = (dayIndex) => {
        const currentDays = productData.contentData.eventDays || [];
        const updatedDays = currentDays.filter((_, i) => i !== dayIndex);
        handleContentDataChange('eventDays', updatedDays);
    };

    // Ticket handling functions (same as before)
    const addTicket = () => {
        const currentTickets = productData.contentData.tickets || [];
        handleContentDataChange('tickets', [...currentTickets, {
            name: '',
            description: '',
            price: 0,
            currency: 'EUR',
            availability: '',
            benefits: [],
            isFree: false
        }]);
    };

    const updateTicket = (ticketIndex, field, value) => {
        const currentTickets = productData.contentData.tickets || [];
        const updatedTickets = [...currentTickets];
        updatedTickets[ticketIndex] = {
            ...updatedTickets[ticketIndex],
            [field]: value
        };
        handleContentDataChange('tickets', updatedTickets);
    };

    const addTicketBenefit = (ticketIndex) => {
        const currentTickets = productData.contentData.tickets || [];
        const updatedTickets = [...currentTickets];
        const currentBenefits = updatedTickets[ticketIndex].benefits || [];
        updatedTickets[ticketIndex].benefits = [...currentBenefits, ''];
        handleContentDataChange('tickets', updatedTickets);
    };

    const updateTicketBenefit = (ticketIndex, benefitIndex, value) => {
        const currentTickets = productData.contentData.tickets || [];
        const updatedTickets = [...currentTickets];
        updatedTickets[ticketIndex].benefits[benefitIndex] = value;
        handleContentDataChange('tickets', updatedTickets);
    };

    const removeTicketBenefit = (ticketIndex, benefitIndex) => {
        const currentTickets = productData.contentData.tickets || [];
        const updatedTickets = [...currentTickets];
        updatedTickets[ticketIndex].benefits = updatedTickets[ticketIndex].benefits.filter((_, i) => i !== benefitIndex);
        handleContentDataChange('tickets', updatedTickets);
    };

    const removeTicket = (ticketIndex) => {
        const currentTickets = productData.contentData.tickets || [];
        const updatedTickets = currentTickets.filter((_, i) => i !== ticketIndex);
        handleContentDataChange('tickets', updatedTickets);
    };

    const handleCreateProduct = async () => {
        if (!validateForm()) {
            // Scroll to first error
            const firstErrorElement = document.querySelector('.error, .sales-error-text');
            if (firstErrorElement) {
                firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setLoading(true);
        try {
            if (editMode && existingProduct) {
                // Update existing product
                const productRef = ref(db, `products/${existingProduct.id}`);
                const updatedProduct = {
                    type: productData.type,
                    subtype: productData.subtype,
                    title: productData.title,
                    description: productData.description,
                    price: parseFloat(productData.price) || 0,
                    currency: productData.currency,
                    contentType: productData.contentType,
                    contentData: productData.contentData,
                    maxPurchases: productData.maxPurchases,
                    accessDuration: productData.accessDuration,
                    status: productData.status,
                    // Discount fields
                    showDiscount: productData.showDiscount || false,
                    discountType: productData.discountType || 'percentage',
                    discountValue: productData.discountValue || '',
                    discountTimeLimited: productData.discountTimeLimited || false,
                    discountExpiryDate: productData.discountExpiryDate || '',
                    discountLabel: productData.discountLabel || 'SAVE',
                    // Stripe-specific fields
                    taxBehavior: productData.taxBehavior || 'exclusive',
                    taxCode: productData.taxCode || '',
                    shippable: productData.shippable || false,
                    statementDescriptor: productData.statementDescriptor || '',
                    unitLabel: productData.unitLabel || '',
                    billingScheme: productData.billingScheme || 'per_unit',
                    // SEO & Marketing
                    metaTitle: productData.metaTitle || '',
                    metaDescription: productData.metaDescription || '',
                    features: productData.features || [],
                    tags: productData.tags || [],
                    updatedAt: serverTimestamp()
                };

                console.log('Updating product with data:', updatedProduct);
                await update(productRef, updatedProduct);
                
                // Emit event to notify ProductInfoWidget about the update
                window.dispatchEvent(new CustomEvent('productUpdated', {
                    detail: { 
                        productId: existingProduct.id,
                        updatedData: updatedProduct,
                        timestamp: new Date().toISOString()
                    }
                }));
                console.log('🚀 ProductCreator - Emitted productUpdated event for:', existingProduct.id);
                
                onProductCreated(existingProduct.id);
            } else {
                // Create new product
                const productRef = push(ref(db, 'products'));
                const newProduct = {
                    providerId: currentUser.uid,
                    type: productData.type,
                    subtype: productData.subtype,
                    title: productData.title,
                    description: productData.description,
                    price: parseFloat(productData.price) || 0,
                    currency: productData.currency,
                    contentType: productData.contentType,
                    contentData: productData.contentData,
                    maxPurchases: productData.maxPurchases,
                    accessDuration: productData.accessDuration,
                    status: productData.status,
                    // Discount fields
                    showDiscount: productData.showDiscount || false,
                    discountType: productData.discountType || 'percentage',
                    discountValue: productData.discountValue || '',
                    discountTimeLimited: productData.discountTimeLimited || false,
                    discountExpiryDate: productData.discountExpiryDate || '',
                    discountLabel: productData.discountLabel || 'SAVE',
                    // Stripe-specific fields
                    taxBehavior: productData.taxBehavior || 'exclusive',
                    taxCode: productData.taxCode || '',
                    shippable: productData.shippable || false,
                    statementDescriptor: productData.statementDescriptor || '',
                    unitLabel: productData.unitLabel || '',
                    billingScheme: productData.billingScheme || 'per_unit',
                    // SEO & Marketing
                    metaTitle: productData.metaTitle || '',
                    metaDescription: productData.metaDescription || '',
                    features: productData.features || [],
                    tags: productData.tags || [],
                    createdAt: serverTimestamp()
                };

                console.log('Creating product with data:', newProduct);
                await set(productRef, newProduct);
                onProductCreated(productRef.key);
            }
        } catch (error) {
            console.error('Error saving product:', error);
            alert('Error saving product. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const productTypeInfo = getProductTypeInfo(productData.type);

    return (
        <div className="sales-product-creator-modal">
            <div className="sales-modal-content-single">
                <div className="sales-modal-header">
                    <div className="sales-modal-title-section">
                        <div className="sales-modal-icon">{productTypeInfo.icon}</div>
                        <div>
                            <h1>{productTypeInfo.title}</h1>
                            <p className="sales-modal-subtitle">{productTypeInfo.subtitle}</p>
                        </div>
                    </div>
                    <button className="sales-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="sales-modal-body-single">
                    <div className="sales-form-content">
                        {/* Basic Information Section */}
                        <div className="sales-form-section">
                            <h3 className="sales-section-title">
                                <span className="sales-section-icon">📝</span>
                                Basic Information
                            </h3>
                            
                            <div className="sales-form-group">
                                <label>Product Type</label>
                                <div className="sales-selected-type">
                                    <div className="sales-type-badge">
                                        {productData.type === 'digital' && '📱 Digital Product'}
                                        {productData.type === 'course' && '🎥 Video Course'}
                                        {productData.type === 'event' && '🎟️ Live Event'}
                                    </div>
                                    <small className="sales-help-text">Selected in previous step</small>
                                </div>
                            </div>

                            <div className="sales-form-group">
                                <label>Category *</label>
                                <select
                                    value={productData.subtype}
                                    onChange={(e) => handleInputChange('subtype', e.target.value)}
                                    className={errors.subtype ? 'error' : ''}
                                >
                                    <option value="">Select a category...</option>
                                    {productSubtypes[productData.type]?.map(subtype => (
                                        <option key={subtype.value} value={subtype.value}>
                                            {subtype.icon} {subtype.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.subtype && <span className="sales-error-text">{errors.subtype}</span>}
                            </div>

                            <div className="sales-form-group">
                                <label>Product Title *</label>
                                <input
                                    type="text"
                                    value={productData.title}
                                    onChange={(e) => handleInputChange('title', e.target.value)}
                                    placeholder="Enter product title"
                                    className={errors.title ? 'error' : ''}
                                />
                                {errors.title && <span className="sales-error-text">{errors.title}</span>}
                            </div>

                            <div className="sales-form-group">
                                <label>Description *</label>
                                <textarea
                                    value={productData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder="Describe your product"
                                    rows={4}
                                    className={errors.description ? 'error' : ''}
                                />
                                {errors.description && <span className="sales-error-text">{errors.description}</span>}
                            </div>
                        </div>

                        {/* Pricing Section - Only for non-events */}
                        {productData.type !== 'event' && (
                            <div className="sales-form-section">
                                <h3 className="sales-section-title">
                                    <span className="sales-section-icon">💰</span>
                                    Pricing & Settings
                                </h3>
                                
                                {/* Free/Paid Toggle */}
                                <div className="sales-form-group">
                                    <label>Product Type</label>
                                    <div className="sales-pricing-toggle">
                                        <div 
                                            className={`sales-pricing-option ${productData.price === 0 ? 'active' : ''}`}
                                            onClick={() => handleInputChange('price', 0)}
                                        >
                                            <div className="sales-pricing-icon">🎉</div>
                                            <div className="sales-pricing-info">
                                                <h4>Free Product</h4>
                                                <p>No payment required</p>
                                            </div>
                                        </div>
                                        <div 
                                            className={`sales-pricing-option ${productData.price > 0 ? 'active' : ''}`}
                                            onClick={() => handleInputChange('price', productData.price > 0 ? productData.price : 9.99)}
                                        >
                                            <div className="sales-pricing-icon">💰</div>
                                            <div className="sales-pricing-info">
                                                <h4>Paid Product</h4>
                                                <p>Requires payment</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Price Input - Only show for paid products */}
                                {productData.price > 0 && (
                                    <div className="sales-form-group">
                                        <label>Price *</label>
                                        <div className="sales-price-input-group">
                                            <input
                                                type="number"
                                                value={productData.price}
                                                onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                                                placeholder="9.99"
                                                min="0.01"
                                                step="0.01"
                                                className={errors.price ? 'error' : ''}
                                            />
                                            <select
                                                value={productData.currency}
                                                onChange={(e) => handleInputChange('currency', e.target.value)}
                                            >
                                                <option value="EUR">EUR</option>
                                                <option value="USD">USD</option>
                                                <option value="GBP">GBP</option>
                                            </select>
                                        </div>
                                        {errors.price && <span className="sales-error-text">{errors.price}</span>}
                                    </div>
                                )}

                                <div className="sales-form-group">
                                    <label>Access Duration</label>
                                    <select
                                        value={productData.accessDuration || ''}
                                        onChange={(e) => handleInputChange('accessDuration', e.target.value ? parseInt(e.target.value) : null)}
                                    >
                                        <option value="">Lifetime Access</option>
                                        <option value="7">7 Days</option>
                                        <option value="30">30 Days</option>
                                        <option value="90">90 Days</option>
                                        <option value="365">1 Year</option>
                                    </select>
                                    <small className="sales-help-text">How long should customers have access?</small>
                                </div>

                                {/* Discount Settings - Only for paid products */}
                                {productData.price > 0 && (
                                    <div className="sales-form-group">
                                        <div className="sales-checkbox-group">
                                            <input
                                                type="checkbox"
                                                id="showDiscount"
                                                checked={productData.showDiscount}
                                                onChange={(e) => handleInputChange('showDiscount', e.target.checked)}
                                            />
                                            <label htmlFor="showDiscount">Enable Discount</label>
                                        </div>
                                        <small className="sales-help-text">Add a discount to this product</small>
                                    </div>
                                )}

                                {productData.price > 0 && productData.showDiscount && (
                                    <>
                                        <div className="sales-form-group">
                                            <label>Discount Type</label>
                                            <select
                                                value={productData.discountType}
                                                onChange={(e) => handleInputChange('discountType', e.target.value)}
                                            >
                                                <option value="percentage">Percentage (%)</option>
                                                <option value="amount">Fixed Amount ({productData.currency})</option>
                                            </select>
                                        </div>

                                        <div className="sales-form-group">
                                            <label>Discount Value *</label>
                                            <input
                                                type="number"
                                                value={productData.discountValue}
                                                onChange={(e) => handleInputChange('discountValue', e.target.value)}
                                                placeholder={productData.discountType === 'percentage' ? 'e.g. 20 (for 20%)' : `e.g. 50 (for 50${productData.currency})`}
                                                min="0"
                                                max={productData.discountType === 'percentage' ? '100' : undefined}
                                            />
                                            <small className="sales-help-text">
                                                {productData.discountType === 'percentage' 
                                                    ? 'Enter percentage (0-100)' 
                                                    : `Enter amount in ${productData.currency}`
                                                }
                                            </small>
                                        </div>

                                        <div className="sales-form-group">
                                            <div className="sales-checkbox-group">
                                                <input
                                                    type="checkbox"
                                                    id="discountTimeLimited"
                                                    checked={productData.discountTimeLimited}
                                                    onChange={(e) => handleInputChange('discountTimeLimited', e.target.checked)}
                                                />
                                                <label htmlFor="discountTimeLimited">Time Limited Discount</label>
                                            </div>
                                            <small className="sales-help-text">Set an expiry date for this discount</small>
                                        </div>

                                        {productData.discountTimeLimited && (
                                            <div className="sales-form-group">
                                                <label>Discount Valid Until *</label>
                                                <input
                                                    type="datetime-local"
                                                    value={productData.discountExpiryDate}
                                                    onChange={(e) => handleInputChange('discountExpiryDate', e.target.value)}
                                                    min={new Date().toISOString().slice(0, 16)}
                                                />
                                                <small className="sales-help-text">After this date, the discount will no longer be applied</small>
                                            </div>
                                        )}

                                        <div className="sales-form-group">
                                            <label>Discount Label</label>
                                            <input
                                                type="text"
                                                value={productData.discountLabel}
                                                onChange={(e) => handleInputChange('discountLabel', e.target.value)}
                                                placeholder="e.g. SAVE, LIMITED OFFER, SALE"
                                                maxLength="20"
                                            />
                                            <small className="sales-help-text">Text displayed on the discount badge</small>
                                        </div>

                                        {/* Discount Preview */}
                                        {productData.price > 0 && productData.discountValue && (
                                            <div className="sales-discount-preview">
                                                <h4>Discount Preview:</h4>
                                                <div className="sales-preview-prices">
                                                    <span className="sales-original-price">
                                                        {new Intl.NumberFormat('de-DE', {
                                                            style: 'currency',
                                                            currency: productData.currency
                                                        }).format(productData.price)}
                                                    </span>
                                                    <span className="sales-discounted-price">
                                                        {new Intl.NumberFormat('de-DE', {
                                                            style: 'currency',
                                                            currency: productData.currency
                                                        }).format(
                                                            productData.discountType === 'percentage' 
                                                                ? productData.price - (productData.price * (parseFloat(productData.discountValue) / 100))
                                                                : Math.max(0, productData.price - parseFloat(productData.discountValue))
                                                        )}
                                                    </span>
                                                    <span className="sales-savings">
                                                        Save {productData.discountType === 'percentage' 
                                                            ? `${productData.discountValue}%` 
                                                            : new Intl.NumberFormat('de-DE', {
                                                                style: 'currency',
                                                                currency: productData.currency
                                                            }).format(parseFloat(productData.discountValue))
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Stripe-Specific Settings for Paid Products */}
                                {productData.price > 0 && (
                                    <>
                                        <div className="sales-section-divider">
                                            <h4>Stripe Payment Settings</h4>
                                            <p>Configure how this product appears in Stripe and on customer statements</p>
                                        </div>

                                        <div className="sales-form-group">
                                            <label>Statement Descriptor</label>
                                            <input
                                                type="text"
                                                value={productData.statementDescriptor}
                                                onChange={(e) => handleInputChange('statementDescriptor', e.target.value)}
                                                placeholder="e.g. MYCOMPANY COURSE"
                                                maxLength="22"
                                            />
                                            <small className="sales-help-text">
                                                What appears on customer's credit card statement (max 22 characters)
                                            </small>
                                        </div>

                                        <div className="sales-form-group">
                                            <label>Tax Behavior</label>
                                            <select
                                                value={productData.taxBehavior}
                                                onChange={(e) => handleInputChange('taxBehavior', e.target.value)}
                                            >
                                                <option value="exclusive">Tax Exclusive (price + tax)</option>
                                                <option value="inclusive">Tax Inclusive (price includes tax)</option>
                                                <option value="unspecified">Unspecified</option>
                                            </select>
                                            <small className="sales-help-text">
                                                How taxes should be calculated on this product
                                            </small>
                                        </div>

                                        <div className="sales-form-group">
                                            <label>Tax Code (Optional)</label>
                                            <input
                                                type="text"
                                                value={productData.taxCode}
                                                onChange={(e) => handleInputChange('taxCode', e.target.value)}
                                                placeholder="e.g. txcd_99999999 or digital_services"
                                            />
                                            <small className="sales-help-text">
                                                Stripe tax code for automatic tax calculation (leave empty for default)
                                            </small>
                                        </div>

                                        <div className="sales-form-group">
                                            <label>Unit Label (Optional)</label>
                                            <input
                                                type="text"
                                                value={productData.unitLabel}
                                                onChange={(e) => handleInputChange('unitLabel', e.target.value)}
                                                placeholder="e.g. per user, per month, per seat"
                                            />
                                            <small className="sales-help-text">
                                                Describes what the customer is purchasing (e.g., "per user")
                                            </small>
                                        </div>
                                    </>
                                )}

                                {/* SEO & Marketing Settings */}
                                <div className="sales-section-divider">
                                    <h4>SEO & Marketing</h4>
                                    <p>Improve discoverability and provide additional product information</p>
                                </div>

                                <div className="sales-form-group">
                                    <label>Meta Title (Optional)</label>
                                    <input
                                        type="text"
                                        value={productData.metaTitle}
                                        onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                                        placeholder="SEO-optimized title for search engines"
                                        maxLength="60"
                                    />
                                    <small className="sales-help-text">
                                        Used for SEO and social media sharing (max 60 characters)
                                    </small>
                                </div>

                                <div className="sales-form-group">
                                    <label>Meta Description (Optional)</label>
                                    <textarea
                                        value={productData.metaDescription}
                                        onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                                        placeholder="Brief description for search engines and social media"
                                        rows="3"
                                        maxLength="160"
                                    />
                                    <small className="sales-help-text">
                                        Used for SEO and social media previews (max 160 characters)
                                    </small>
                                </div>

                                <div className="sales-form-group">
                                    <label>Product Tags (Optional)</label>
                                    <input
                                        type="text"
                                        value={productData.tags.join(', ')}
                                        onChange={(e) => handleInputChange('tags', e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag))}
                                        placeholder="e.g. course, digital, marketing, beginner"
                                    />
                                    <small className="sales-help-text">
                                        Comma-separated tags for categorization and filtering
                                    </small>
                                </div>

                                <div className="sales-form-group">
                                    <label>Maximum Sales</label>
                                    <input
                                        type="number"
                                        value={productData.maxPurchases || ''}
                                        onChange={(e) => handleInputChange('maxPurchases', e.target.value ? parseInt(e.target.value) : null)}
                                        placeholder="Unlimited"
                                        min="1"
                                    />
                                    <small className="sales-help-text">Limit the total number of sales (leave empty for unlimited)</small>
                                </div>
                            </div>
                        )}

                        {/* Event pricing explanation */}
                        {productData.type === 'event' && (
                            <div className="sales-form-section">
                                <h3 className="sales-section-title">
                                    <span className="sales-section-icon">🎫</span>
                                    Event Pricing
                                </h3>
                                <div className="sales-info-card">
                                    <div className="sales-info-icon">🎫</div>
                                    <div>
                                        <h4>Ticket-Based Pricing</h4>
                                        <p>For events, pricing is handled through individual ticket types. You can create multiple ticket tiers with different prices and benefits in the content section below.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Content Section */}
                        <div className="sales-form-section">
                            <h3 className="sales-section-title">
                                <span className="sales-section-icon">
                                    {productData.type === 'digital' && '📁'}
                                    {productData.type === 'course' && '🎬'}
                                    {productData.type === 'event' && '📅'}
                                </span>
                                Content Setup
                            </h3>
                            
                            {/* Digital Product Content */}
                            {productData.type === 'digital' && (
                                <div className="sales-content-section">
                                    <h4>Upload Your Files</h4>
                                    <FileUploader onFileUploaded={handleFileUploaded} />
                                    
                                    {uploadedFiles.length > 0 && (
                                        <div className="sales-uploaded-files">
                                            <h5>Uploaded Files:</h5>
                                            {uploadedFiles.map((file, index) => (
                                                <div key={index} className="sales-uploaded-file">
                                                    <div className="sales-file-info">
                                                        <div className="sales-file-icon">
                                                            {file.type.includes('pdf') ? 'PDF' : 
                                                             file.type.includes('image') ? 'IMG' : 
                                                             file.type.includes('zip') ? 'ZIP' : 'FILE'}
                                                        </div>
                                                        <div className="sales-file-details">
                                                            <h6>{file.name}</h6>
                                                            <p>{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                                                        </div>
                                                    </div>
                                                    <button 
                                                        type="button" 
                                                        className="sales-file-remove"
                                                        onClick={() => removeUploadedFile(index)}
                                                        title="Remove file"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Course Content */}
                            {productData.type === 'course' && (
                                <div className="sales-content-section">
                                    <h4>Course Structure</h4>
                                    <p className="sales-section-description">Build your course with video lessons and supporting materials</p>
                                    
                                    {(productData.contentData.videoModules || []).map((module, index) => (
                                        <div key={index} className="sales-video-module-card">
                                            <div className="sales-module-header">
                                                <div className="sales-module-number">Module {index + 1}</div>
                                                <button 
                                                    type="button" 
                                                    className="sales-module-remove"
                                                    onClick={() => removeVideoModule(index)}
                                                    title="Remove module"
                                                >
                                                    ✕
                                                </button>
                                            </div>

                                            <div className="sales-module-content">
                                                <div className="sales-form-row">
                                                    <div className="sales-form-group">
                                                        <label>Module Title *</label>
                                                        <input
                                                            type="text"
                                                            value={module.title}
                                                            onChange={(e) => updateVideoModule(index, 'title', e.target.value)}
                                                            placeholder="e.g. Introduction to the Course"
                                                        />
                                                    </div>
                                                    <div className="sales-form-group sales-duration-group">
                                                        <label>Duration</label>
                                                        <input
                                                            type="text"
                                                            value={module.duration || ''}
                                                            onChange={(e) => updateVideoModule(index, 'duration', e.target.value)}
                                                            placeholder="e.g. 15 min"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="sales-form-group">
                                                    <label>Video URL *</label>
                                                    <input
                                                        type="url"
                                                        value={module.url}
                                                        onChange={(e) => updateVideoModule(index, 'url', e.target.value)}
                                                        placeholder="https://youtube.com/watch?v=... or Vimeo/other"
                                                    />
                                                </div>

                                                <div className="sales-form-group">
                                                    <label>Module Description</label>
                                                    <textarea
                                                        value={module.description}
                                                        onChange={(e) => updateVideoModule(index, 'description', e.target.value)}
                                                        placeholder="What will students learn in this module?"
                                                        rows={3}
                                                    />
                                                </div>

                                                <div className="sales-module-documents">
                                                    <label>Supporting Documents</label>
                                                    <MiniFileUploader 
                                                        onFileUploaded={(docData) => handleModuleDocumentUploaded(index, docData)}
                                                        placeholder="Upload PDF, slides, or worksheets"
                                                    />
                                                    
                                                    {module.documents && module.documents.length > 0 && (
                                                        <div className="sales-module-docs-list">
                                                            {module.documents.map((doc, docIndex) => (
                                                                <div key={docIndex} className="sales-module-doc-item">
                                                                    <div className="sales-doc-info">
                                                                        <div className="sales-doc-icon">
                                                                            {doc.type.includes('pdf') ? '📄' : 
                                                                             doc.type.includes('doc') ? '📝' : 
                                                                             doc.type.includes('ppt') ? '📊' : '📎'}
                                                                        </div>
                                                                        <span className="sales-doc-name">{doc.name}</span>
                                                                    </div>
                                                                    <button 
                                                                        type="button" 
                                                                        className="sales-doc-remove"
                                                                        onClick={() => removeModuleDocument(index, docIndex)}
                                                                        title="Remove document"
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Module Feature Points */}
                                                <div className="sales-module-features">
                                                    <div className="sales-features-header">
                                                        <label>Learning Outcomes & Features</label>
                                                        <button 
                                                            type="button" 
                                                            className="sales-add-feature-btn"
                                                            onClick={() => addModuleFeaturePoint(index)}
                                                            title="Add feature point"
                                                        >
                                                            <i className="fas fa-plus"></i>
                                                        </button>
                                                    </div>
                                                    
                                                    {(module.featurePoints || []).map((feature, featureIndex) => (
                                                        <div key={featureIndex} className="sales-feature-point-item">
                                                            <div className="sales-feature-icon-select">
                                                                <select
                                                                    value={feature.icon}
                                                                    onChange={(e) => updateModuleFeaturePoint(index, featureIndex, 'icon', e.target.value)}
                                                                >
                                                                    <option value="fas fa-check">✓ Check</option>
                                                                    <option value="fas fa-star">★ Star</option>
                                                                    <option value="fas fa-lightbulb">💡 Idea</option>
                                                                    <option value="fas fa-graduation-cap">🎓 Learn</option>
                                                                    <option value="fas fa-rocket">🚀 Boost</option>
                                                                    <option value="fas fa-target">🎯 Goal</option>
                                                                    <option value="fas fa-brain">🧠 Skill</option>
                                                                    <option value="fas fa-trophy">🏆 Achievement</option>
                                                                    <option value="fas fa-chart-line">📈 Growth</option>
                                                                    <option value="fas fa-tools">🔧 Tool</option>
                                                                </select>
                                                            </div>
                                                            <input
                                                                type="text"
                                                                value={feature.text}
                                                                onChange={(e) => updateModuleFeaturePoint(index, featureIndex, 'text', e.target.value)}
                                                                placeholder="e.g. Master the fundamentals of..."
                                                                className="sales-feature-text-input"
                                                            />
                                                            <button 
                                                                type="button" 
                                                                className="sales-feature-remove"
                                                                onClick={() => removeModuleFeaturePoint(index, featureIndex)}
                                                                title="Remove feature"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    ))}
                                                    
                                                    {(!module.featurePoints || module.featurePoints.length === 0) && (
                                                        <div className="sales-no-features">
                                                            <p>No learning outcomes added yet. Click + to add what students will learn in this module.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    <button 
                                        type="button" 
                                        className="sales-add-btn primary"
                                        onClick={addVideoModule}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="12" y1="5" x2="12" y2="19"/>
                                            <line x1="5" y1="12" x2="19" y2="12"/>
                                        </svg>
                                        Add Video Module
                                    </button>
                                </div>
                            )}

                            {/* Event Content */}
                            {productData.type === 'event' && (
                                <EventBuilder
                                    eventData={productData.contentData}
                                    onEventDataChange={handleContentDataChange}
                                    onAddEventDay={addEventDay}
                                    onUpdateEventDay={updateEventDay}
                                    onRemoveEventDay={removeEventDay}
                                    onAddTimeSlot={addTimeSlot}
                                    onUpdateTimeSlot={updateTimeSlot}
                                    onRemoveTimeSlot={removeTimeSlot}
                                    onAddTicket={addTicket}
                                    onUpdateTicket={updateTicket}
                                    onRemoveTicket={removeTicket}
                                    onAddTicketBenefit={addTicketBenefit}
                                    onUpdateTicketBenefit={updateTicketBenefit}
                                    onRemoveTicketBenefit={removeTicketBenefit}
                                />
                            )}

                            {errors.content && <span className="sales-error-text">{errors.content}</span>}
                        </div>
                    </div>
                </div>

                <div className="sales-modal-footer-single">
                    <button 
                        type="button" 
                        className="sales-create-btn"
                        onClick={handleCreateProduct}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <div className="sales-loading-spinner"></div>
                                {editMode ? 'Updating...' : 'Creating...'}
                            </>
                        ) : (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 6L9 17l-5-5"/>
                                </svg>
                                {editMode ? 'Update Product' : `Create ${productTypeInfo.title.replace('Create ', '')}`}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductCreator;
